import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, SpinnerGap, X } from '@phosphor-icons/react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  getInvitedItineraryEventIds,
  normalizeGuestPartyResponse,
} from './guestPartyUtils';

const GuestItineraryRsvpContext = createContext(null);

const normalizeStatus = (status) => (
  status === 'confirmed' || status === 'declined' ? status : 'pending'
);

export function GuestItineraryRsvpProvider({ eventId, enabled, onInvitationScopeLoaded, children }) {
  const [loading, setLoading] = useState(Boolean(enabled));
  const [primaryGuestId, setPrimaryGuestId] = useState(null);
  const [needsIdentitySelection, setNeedsIdentitySelection] = useState(false);
  const [invitedItineraryIds, setInvitedItineraryIds] = useState([]);
  const [statusByItemId, setStatusByItemId] = useState({});
  const [savingItemId, setSavingItemId] = useState(null);

  useEffect(() => {
    if (!enabled || !eventId) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);

    const load = async () => {
      try {
        const groupResponse = await api.get(`/events/${eventId}/my-guest-group`);
        if (!active) return;
        const party = normalizeGuestPartyResponse(groupResponse.data);
        const nextPrimaryGuestId = party.primaryGuestId;
        setPrimaryGuestId(nextPrimaryGuestId);
        setNeedsIdentitySelection(party.group.length > 1 && !nextPrimaryGuestId);
        const nextInvitedItineraryIds = getInvitedItineraryEventIds(groupResponse.data, party.group);
        setInvitedItineraryIds(nextInvitedItineraryIds);
        onInvitationScopeLoaded?.(nextInvitedItineraryIds);

        if (nextPrimaryGuestId) {
          const rsvpResponse = await api.get(`/events/${eventId}/rsvp`, {
            params: { guest_id: nextPrimaryGuestId },
          });
          if (!active) return;
          setStatusByItemId((rsvpResponse.data || []).reduce((statuses, rsvp) => {
            if (rsvp.itinerary_item_id) {
              statuses[rsvp.itinerary_item_id] = normalizeStatus(rsvp.status);
            }
            return statuses;
          }, {}));
        }
      } catch (error) {
        console.error('Failed to load itinerary RSVP options', error);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [enabled, eventId, onInvitationScopeLoaded]);

  const updateRsvp = useCallback(async (itemId, status) => {
    if (!primaryGuestId || savingItemId) return;
    const previousStatus = statusByItemId[itemId];
    setSavingItemId(itemId);
    setStatusByItemId((current) => ({ ...current, [itemId]: status }));

    try {
      const response = await api.post(`/events/${eventId}/rsvp`, {
        guest_id: primaryGuestId,
        itinerary_item_id: itemId,
        status,
      });
      setStatusByItemId((current) => ({
        ...current,
        [itemId]: normalizeStatus(response.data?.status || status),
      }));
      toast.success(status === 'confirmed' ? 'RSVP confirmed' : 'Regrets sent');
    } catch (error) {
      setStatusByItemId((current) => {
        const next = { ...current };
        if (previousStatus) next[itemId] = previousStatus;
        else delete next[itemId];
        return next;
      });
      toast.error(error.response?.data?.detail || 'Failed to update RSVP');
    } finally {
      setSavingItemId(null);
    }
  }, [eventId, primaryGuestId, savingItemId, statusByItemId]);

  const value = useMemo(() => ({
    enabled,
    loading,
    primaryGuestId,
    needsIdentitySelection,
    invitedItineraryIds,
    statusByItemId,
    savingItemId,
    updateRsvp,
  }), [
    enabled,
    invitedItineraryIds,
    loading,
    needsIdentitySelection,
    primaryGuestId,
    savingItemId,
    statusByItemId,
    updateRsvp,
  ]);

  return (
    <GuestItineraryRsvpContext.Provider value={value}>
      {children}
    </GuestItineraryRsvpContext.Provider>
  );
}

export function GuestItineraryRsvpControls({ itemId }) {
  const context = useContext(GuestItineraryRsvpContext);
  if (!context?.enabled) return null;

  if (context.loading) {
    return (
      <div className="mt-4 inline-flex items-center gap-2 text-xs text-muted-foreground" role="status">
        <SpinnerGap className="h-4 w-4 animate-spin" /> Loading RSVP...
      </div>
    );
  }

  if (context.needsIdentitySelection || !context.primaryGuestId) {
    return (
      <Button asChild variant="outline" size="sm" className="mt-4 rounded-full">
        <Link to="/guest/rsvp">Choose guest profile to RSVP</Link>
      </Button>
    );
  }

  if (!context.invitedItineraryIds.includes(itemId)) {
    return (
      <p className="mt-4 text-xs text-muted-foreground">Not included in your invitation</p>
    );
  }

  const status = normalizeStatus(context.statusByItemId[itemId]);
  const isSaving = context.savingItemId === itemId;

  return (
    <div className="mt-4 flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`itinerary-rsvp-${itemId}`}>
      <Badge
        variant="outline"
        className={status === 'confirmed'
          ? 'w-fit border-emerald-200 bg-emerald-50 text-emerald-700'
          : status === 'declined'
            ? 'w-fit border-rose-200 bg-rose-50 text-rose-700'
            : 'w-fit border-amber-200 bg-amber-50 text-amber-700'}
      >
        {status === 'confirmed' ? 'Will attend' : status === 'declined' ? 'Regrets sent' : 'Awaiting reply'}
      </Badge>
      <div className="grid grid-cols-2 gap-2 self-end sm:ml-auto sm:flex sm:justify-end">
        <Button
          type="button"
          size="sm"
          variant={status === 'confirmed' ? 'default' : 'outline'}
          onClick={() => context.updateRsvp(itemId, 'confirmed')}
          disabled={isSaving}
          aria-pressed={status === 'confirmed'}
        >
          {isSaving ? <SpinnerGap className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
          Will attend
        </Button>
        <Button
          type="button"
          size="sm"
          variant={status === 'declined' ? 'destructive' : 'outline'}
          onClick={() => context.updateRsvp(itemId, 'declined')}
          disabled={isSaving}
          aria-pressed={status === 'declined'}
        >
          <X className="mr-1 h-4 w-4" />
          Send regrets
        </Button>
      </div>
    </div>
  );
}
